import * as htmlToImage from "html-to-image";

/**
 * Generates an image from an HTML element using html-to-image
 * @param elementId - The ID of the element to capture
 * @returns A Blob containing the PNG image, or null if generation fails
 */
export const generateShareImage = async (elementId: string): Promise<Blob | null> => {
  const element = document.getElementById(elementId);
  if (!element) {
    console.error(`Element with id "${elementId}" not found`);
    return null;
  }

  try {
    // Use html-to-image which handles modern CSS much better
    const blob = await htmlToImage.toBlob(element, {
      cacheBust: true,
      pixelRatio: 2, // High quality for retina displays
      backgroundColor: "#0a0a0f", // Match card background
    });

    if (!blob) {
      throw new Error("Failed to generate image blob");
    }

    console.log("Generated blob successfully:", blob.size, "bytes");
    return blob;
  } catch (error) {
    console.error("Error generating share image:", error);
    return null;
  }
};

/**
 * Downloads an image blob to the user's device
 * @param blob - The image blob to download
 * @param filename - The filename to save as
 */
export const downloadImage = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Copies an image blob to clipboard
 * @param blob - The image blob to copy
 * @returns true if successful, false otherwise
 */
export const copyImageToClipboard = async (blob: Blob): Promise<boolean> => {
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        [blob.type]: blob,
      }),
    ]);
    return true;
  } catch (error) {
    console.error("Failed to copy image to clipboard:", error);
    return false;
  }
};

/**
 * Shares to Twitter/X - uses Web Share on mobile, clipboard on desktop
 * @param blob - The image blob to share
 * @param text - The pre-filled tweet text
 * @returns "shared" if Web Share worked, "copied" if clipboard used, "failed" otherwise
 */
export const shareToTwitter = async (blob: Blob, text: string): Promise<"shared" | "copied" | "failed"> => {
  // On mobile, try Web Share API first (opens Twitter app directly with image)
  if (navigator.share && navigator.canShare) {
    try {
      const file = new File([blob], "nordex-trade.png", { type: "image/png" });
      
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({
          text: text,
          files: [file],
        });
        return "shared";
      }
    } catch (error) {
      // User cancelled or share failed, fall through to clipboard method
      console.log("Web Share cancelled or failed:", error);
    }
  }
  
  // Desktop fallback: Copy to clipboard and open Twitter
  const copied = await copyImageToClipboard(blob);
  
  // Open Twitter with pre-filled text
  const twitterText = encodeURIComponent(text);
  const twitterUrl = `https://twitter.com/intent/tweet?text=${twitterText}`;
  window.open(twitterUrl, "_blank", "noopener,noreferrer");
  
  return copied ? "copied" : "failed";
};

/**
 * Shares via the Web Share API if available, otherwise downloads the image
 * @param blob - The image blob to share
 * @param _text - Reserved for future use (not currently used to avoid duplicate clipboard items)
 */
export const shareViaWebShare = async (blob: Blob, _text?: string) => {
  // Check if Web Share API is available and supports files
  if (navigator.share && navigator.canShare) {
    try {
      const file = new File([blob], "nordex-trade.png", { type: "image/png" });
      
      // Check if we can share this file
      if (navigator.canShare({ files: [file] })) {
        // Share only the image file to avoid duplicate clipboard items
        await navigator.share({
          files: [file],
        });
        return;
      }
    } catch (error) {
      // If sharing fails or is cancelled, fall through to download
      console.log("Share cancelled or failed:", error);
    }
  }
  
  // Fallback to download if Web Share API is not available or fails
  downloadImage(blob, "nordex-trade.png");
};
